import { ColumnDef } from "@tanstack/react-table";
import { Download, Show, AlertCircle} from "@nike/nike-design-system-icons";
import {DeclarationPeriodEvent} from "../../shared/Types";
import {IconButton, Tooltip} from "@nike/eds";
import { isPreviewAvailable } from "../../shared/utils";
import DateTimeFormat from "../shared/DateTimeFormat";

export type DeclarationPeriodEventsTableColumn<T> = ColumnDef<T, any> & {
    header: string | React.ReactNode | (() => React.ReactNode);
    accessorKey?: keyof T | string;
    filterLabel?: string;
    filterType?: "select" | "text" | "date" | "dateRange" | "monthRange" | "month";
    filterOptions?: Array<{ value: string; label: string }>;
    getFilterOptions?: (tableState: any) => Array<{ value: string; label: string }>;
    enableHiding?: boolean;
    enableSorting?: boolean;
};

export const getDeclarationPeriodEventsTableColumns = (
    onDownload?: (s3Reference: string) => void,
    onShowPayload?: (s3Reference: string) => void,
): DeclarationPeriodEventsTableColumn<DeclarationPeriodEvent>[] => [
    {
        header: () => (<span title="Event Timestamp">Event Timestamp</span>),
        accessorKey: "timestamp",
        cell: ({ row }) => <DateTimeFormat timestamp={row.original.eventTime} format="dd/MM/yyyy, HH:mm" />,
        size: 180,
        enableSorting: false,
        enableColumnFilter: false,
    },
    {
        header: () => (<span title="Declaration Id">Declaration Id</span>),
        accessorKey: "declarationId",
        size: 200,
        enableSorting: false,
        enableColumnFilter: false,
    },
    {
        header: () => (<span title="Flow Step">Flow Step</span>),
        accessorKey: "flowStep",
        size: 200,
        enableSorting: false,
        enableColumnFilter: false,
    },
    {
        header: () => (<span title="Status">Status</span>),
        accessorKey: "status",
        size: 120,
        enableSorting: false,
        enableColumnFilter: false,
    },
    {
        header: () => (<span title="Local Reference Number">LRN</span>),
        accessorKey: "lrn",
        size: 120,
        enableSorting: false,
        enableColumnFilter: false,
    },
    {
        header: () => (<span title="Movement Reference Number">MRN</span>),
        accessorKey: "mrn",
        size: 120,
        enableSorting: false,
        enableColumnFilter: false,
    },
    {
        header: () => (<span title="Payload">Payload</span>),
        id: "payload-actions",
        cell: ({ row }) => (
            <div>
                <IconButton
                    icon={<Download />}
                    label="Download payload"
                    size="small"
                    variant="ghost"
                    disabled={!row.original.s3Reference}
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        if (row.original.s3Reference && onDownload) {
                            onDownload(row.original.s3Reference);
                        }
                    }}
                />
                &nbsp;
                {!row.original.s3Reference || !isPreviewAvailable(row.original.payloadSize) ? (
                    <Tooltip bodySlot="Payload too large for viewing. Use download instead.">
                        <IconButton
                            icon={<Show />}
                            label="View payload"
                            size="small"
                            variant="ghost"
                            disabled
                        />
                    </Tooltip>
                ) : (
                    <IconButton
                        icon={<Show />}
                        label="View payload"
                        size="small"
                        variant="ghost"
                        onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            if (onShowPayload) {
                                onShowPayload(row.original.s3Reference);
                            }
                        }}
                    />
                )}
            </div>
        ),
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
    },
    {
        header: () => (<span title="Error">Error</span>),
        accessorKey: "error_message",
        cell: ({row}) => {
            return row.original.errorMessage != null ?
                <Tooltip bodySlot={row.original.errorMessage}>
                    <AlertCircle />
                </Tooltip> : null;
        },
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
    }
];
